import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import React, { useEffect, useMemo } from 'react';
import { ExplorerUpsertDigitalDossierFormsAndDocsInformationDto } from '../../../../../services/types/query-params';
import { useDigitalDossierSectionContext } from '../../../context/DigitalDossierSectionContext';
import { mapDocumentationAndFormsSerializerToDTO } from '../utils';
import { SubmitHandler, useForm } from '@agrotoken-tech/ui';
import {
  CLIENT_DIGITAL_DOSSIER_SECTIONS_STATUS,
  ValidationStatus,
} from '@bcba/types';
import { useToastsHook, useTranslation } from '@bcba/react-hooks';
import {
  QueryObserverResult,
  RefetchOptions,
  useMutation,
} from '@tanstack/react-query';
import { updateFormsAndDocsInformation } from '../../../../../services/digital-dossiers/update-information';
import { ExplorerDigitalDossierFormsAndDocsInformationSerializer } from '@bcba/serializers';
import { useDigitalDossierActionButtons } from '../../../hooks/useDigitalDossierActionButtons';
import { usePendingChangesAlert } from '../../../../../contexts/hooks';

export interface DocumentationAndFormsFormProps {
  data: ExplorerDigitalDossierFormsAndDocsInformationSerializer;
  refetch: (
    options?: RefetchOptions | undefined,
  ) => Promise<
    QueryObserverResult<
      ExplorerDigitalDossierFormsAndDocsInformationSerializer,
      Error
    >
  >;
}

const resolver = classValidatorResolver(
  ExplorerUpsertDigitalDossierFormsAndDocsInformationDto,
  { transformer: { excludeExtraneousValues: true } },
);

export const useDocumentationAndFormsForm = ({
  data,
  refetch,
}: DocumentationAndFormsFormProps) => {
  const { t } = useTranslation();
  const { showSuccessToast } = useToastsHook();
  const { refetchDigitalDossierStatus } = useDigitalDossierSectionContext();

  const apiDataMappedToLocalState = useMemo(() => {
    return mapDocumentationAndFormsSerializerToDTO(data);
  }, [JSON.stringify(data)]);

  const formMethods = useForm({
    resolver,
    defaultValues: apiDataMappedToLocalState,
  });

  const {
    reset,
    handleSubmit,
    formState: { isDirty },
  } = formMethods;
  usePendingChangesAlert(isDirty);
  const { fileBoxNumber } = data;

  const status =
    data?.statusLabels as keyof typeof CLIENT_DIGITAL_DOSSIER_SECTIONS_STATUS;
  const validationMessage = data?.validationMessage;

  const isPendingChangeRequest =
    status === CLIENT_DIGITAL_DOSSIER_SECTIONS_STATUS.PENDING_REVIEW;

  const isApproved = status === CLIENT_DIGITAL_DOSSIER_SECTIONS_STATUS.APPROVED;

  const { mutate: saveChanges, isPending: saveIsPending } = useMutation({
    mutationFn: (
      formData: ExplorerUpsertDigitalDossierFormsAndDocsInformationDto,
    ) => updateFormsAndDocsInformation(formData),
    onSuccess: async () => {
      refetchDigitalDossierStatus();
      showSuccessToast({
        title: t(
          'explorer.digitalDossier.formsAndDocsInfo.update.success.toast',
        ),
      });
      const { data } = await refetch();
      const updatedData = mapDocumentationAndFormsSerializerToDTO(data);
      reset(updatedData, { keepDirty: false });
    },
  });

  const handleSave: SubmitHandler<any> = (data: any) => {
    // Filter out expirationDate and identifier from docs.
    const keys = ['sisaFile', 'statuteFile'];
    keys.forEach((key) => {
      if (data[key]) {
        data[key].expirationDate = undefined;
        data[key].identifier = undefined;
        data[key].documentUuid = undefined;
      }
    });
    const statuteChangeFiles = data['statuteChangeFiles'];
    if (statuteChangeFiles) {
      statuteChangeFiles.forEach((doc: any) => {
        doc.expirationDate = undefined;
        doc.identifier = undefined;
        doc.documentUuid = undefined;
      });
    }

    const assemblyBoardMinutesFiles = data['assemblyBoardMinutesFiles'];
    if (assemblyBoardMinutesFiles) {
      assemblyBoardMinutesFiles.forEach((doc: any) => {
        doc.expirationDate = undefined;
        doc.identifier = undefined;
        doc.documentUuid = undefined;
      });
    }

    const powerOfAttorneyFiles = data['powerOfAttorneyFiles'];
    if (powerOfAttorneyFiles) {
      powerOfAttorneyFiles.forEach((doc: any) => {
        doc.expirationDate = undefined;
        doc.identifier = undefined;
        doc.documentUuid = undefined;
      });
    }

    const additionalDocumentFiles = data['additionalDocumentFiles'];
    if (additionalDocumentFiles) {
      additionalDocumentFiles.forEach((doc: any) => {
        doc.expirationDate = undefined;
        doc.identifier = undefined;
        doc.documentUuid = undefined;
      });
    }

    saveChanges(data);
  };

  const { actionButtons, isFormReadOnly } = useDigitalDossierActionButtons({
    status: status as ValidationStatus,
    isLoadingSave: saveIsPending,
    formHasChanges: isDirty,
    handleSave: handleSubmit(handleSave),
  });

  const allowFormEdition =
    !isApproved || isPendingChangeRequest || !isFormReadOnly;

  // This is to reset form after submit.
  // We had a problem trying to reset form on mutation success, newly added file names were not displayed correctly
  useEffect(() => {
    reset(apiDataMappedToLocalState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiDataMappedToLocalState]);

  return {
    formMethods,
    isPendingChangeRequest,
    validationMessage,
    allowFormEdition,
    actionButtons,
    isFormReadOnly,
    fileBoxNumber,
  };
};

export default useDocumentationAndFormsForm;
